import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * P4b: WatchlistPanel — TradingView-style watchlist (F1: adapter + VN30 fallback).
 * Rows: symbol, sparkline, price, change%. Click switches chart symbol.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import { VN30_SYMBOLS, VN30_NAMES } from "../../constants/vn30";
import { formatChangeValue, formatPercent, formatPrice } from "../../utils/numberFormat";
import { SymbolDetailsSection } from "./SymbolDetailsSection";
import styles from "../../styles/watchlist-panel.module.css";
function mergeUpdate(item, update) {
    const defined = Object.fromEntries(Object.entries(update).filter(([, value]) => value !== undefined));
    return { ...item, ...defined };
}
export function WatchlistPanel({ symbol, onSymbolChange, adapter, datum }) {
    const { t } = useWidgetI18n();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    // D3: roving tabindex for APG listbox keyboard nav.
    const [focusIndex, setFocusIndex] = useState(0);
    const rowRefs = useRef([]);
    const activeSymbol = symbol.toUpperCase();
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const fallback = () => VN30_SYMBOLS.map((s) => ({ symbol: s, name: VN30_NAMES[s] ?? s }));
        if (!adapter) {
            setItems(fallback());
            setLoading(false);
            return;
        }
        Promise.resolve()
            .then(() => adapter.getWatchlist())
            .then((list) => { if (!cancelled && Array.isArray(list))
            setItems(list); })
            .catch(() => { if (!cancelled)
            setItems(fallback()); })
            .finally(() => { if (!cancelled)
            setLoading(false); });
        return () => { cancelled = true; };
    }, [adapter]);
    const symbols = useMemo(() => items.map((item) => item.symbol), [items]);
    const symbolsKey = symbols.join(",");
    const symbolsForSub = useMemo(() => symbolsKey.split(",").filter(Boolean), [symbolsKey]);
    useEffect(() => {
        if (!adapter?.subscribeQuotes || symbolsForSub.length === 0)
            return;
        const off = adapter.subscribeQuotes(symbolsForSub, (update) => {
            setItems((prev) => prev.map((item) => item.symbol === update.symbol ? mergeUpdate(item, update) : item));
        });
        return () => { if (typeof off === "function")
            off(); };
    }, [adapter, symbolsForSub]);
    if (loading) {
        return _jsx("div", { className: styles.empty, children: t("widget.loading") });
    }
    if (items.length === 0) {
        return _jsx("div", { className: styles.empty, children: t("widget.noData") });
    }
    const focusRow = (index) => {
        const clamped = Math.max(0, Math.min(items.length - 1, index));
        setFocusIndex(clamped);
        rowRefs.current[clamped]?.focus();
    };
    const handleListKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            focusRow(focusIndex + 1);
        }
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            focusRow(focusIndex - 1);
        }
        else if (e.key === "Home") {
            e.preventDefault();
            focusRow(0);
        }
        else if (e.key === "End") {
            e.preventDefault();
            focusRow(items.length - 1);
        }
        else if (e.key === "Enter") {
            const item = items[focusIndex];
            if (item)
                onSymbolChange?.(item.symbol);
        }
    };
    const activeItem = items.find((item) => item.symbol.toUpperCase() === activeSymbol);
    return (_jsxs("div", { className: styles.root, children: [_jsxs("div", { className: styles.colHead, "data-testid": "watchlist-col-head", "aria-hidden": "true", children: [_jsx("span", { className: styles.colHeadName }), _jsx("span", { className: styles.col, "data-col": "last", children: t("dock.colLast") }), _jsx("span", { className: styles.col, "data-col": "change", children: t("dock.colChange") }), _jsx("span", { className: styles.col, "data-col": "pct", children: t("dock.colChangePct") })] }), _jsx("div", { className: styles.list, role: "listbox", "aria-label": t("dock.watchlist"), onKeyDown: handleListKeyDown, children: items.map((item, index) => {
                    const isActive = item.symbol.toUpperCase() === activeSymbol;
                    const last = typeof item.price === "number" && Number.isFinite(item.price)
                        ? formatPrice(item.price)
                        : "—";
                    const change = typeof item.change === "number" && Number.isFinite(item.change)
                        ? formatChangeValue(item.change)
                        : "—";
                    const pct = typeof item.changePercent === "number" && Number.isFinite(item.changePercent)
                        ? formatPercent(item.changePercent)
                        : "—";
                    const sign = typeof item.change === "number" && Number.isFinite(item.change)
                        ? item.change
                        : typeof item.changePercent === "number" && Number.isFinite(item.changePercent)
                            ? item.changePercent
                            : null;
                    const cellClass = sign === null ? undefined : sign > 0 ? styles.up : sign < 0 ? styles.down : undefined;
                    return (_jsxs("div", { role: "option", "aria-selected": isActive, tabIndex: index === focusIndex ? 0 : -1, ref: (el) => { rowRefs.current[index] = el; }, "data-testid": `watchlist-row-${item.symbol}`, className: `${styles.row}${isActive ? ` ${styles.rowActive}` : ""}`, onClick: () => { setFocusIndex(index); onSymbolChange?.(item.symbol); }, title: item.name ?? item.symbol, children: [_jsxs("span", { className: styles.symBlock, children: [_jsx("span", { className: styles.sym, children: item.symbol }), item.name ? _jsx("span", { className: styles.name, children: item.name }) : null] }), _jsx("span", { className: styles.cell, "data-cell": "last", title: `${t("dock.colLast")}: ${last}`, children: last }), _jsx("span", { className: `${styles.cell}${cellClass ? ` ${cellClass}` : ""}`, "data-cell": "change", title: `${t("dock.colChange")}: ${change}`, children: change }), _jsx("span", { className: `${styles.cell}${cellClass ? ` ${cellClass}` : ""}`, "data-cell": "pct", title: `${t("dock.colChangePct")}: ${pct}`, children: pct })] }, item.symbol));
                }) }), _jsx(SymbolDetailsSection, { symbol: activeSymbol, item: activeItem, datum: datum })] }));
}
